/**
 * UserGroupsTab
 * Tab content for the "Användargrupper" tab in Hantera användare (stiftelse
 * instances only): the existing groups under `users/groups` with their
 * internal members, where admins add and remove users. Groups are not
 * created or edited here. Membership narrows the iOS Service and inventory
 * views to the buildings a group is responsible for.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Form } from 'react-bootstrap';
import { ChevronLeft, Plus, Users, X } from 'lucide-react';
import { useIsSmallScreen } from '../../hooks/useIsSmallScreen';
import { useListDetailScroll } from '../../hooks/useListDetailScroll';
import { useUserGroups } from '../../hooks/useUserGroups';
import { Userprofile } from '../../Helpers/UsersContext';
import { PageLoader } from '../../Components/LoadingSpinner';

interface UserGroupsTabProps {
    /** Active internal users (the parent already filters out deleted and external ones) */
    users: Userprofile[];
    selectedGroupKey?: string;
    /** URL of one group (null = the list); the list items are links to it */
    groupPath: (groupKey: string | null) => string;
    /** Reloads the user list after a membership change so counts and members update */
    onMembershipChanged: () => Promise<void>;
}

const getInitials = (name: string) => {
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
};

const groupsOf = (user: Userprofile): string[] => (Array.isArray(user.groups) ? user.groups : []);

const memberCountLabel = (count: number) => (count === 1 ? '1 användare' : `${count} användare`);

const UserGroupsTab: React.FC<UserGroupsTabProps> = ({ users, selectedGroupKey, groupPath, onMembershipChanged }) => {
    const { groups, loading, error, submitting, setUserGroups, clearError } = useUserGroups();
    const [userToAdd, setUserToAdd] = useState('');

    const selectedGroup = selectedGroupKey ? groups.find(g => g.key === selectedGroupKey) ?? null : null;

    // Phones show either the group list or the open group, never both
    const isSmallScreen = useIsSmallScreen();
    const mobileView = isSmallScreen && selectedGroup ? 'detail' : 'list';
    const { containerRef, rememberListScroll } = useListDetailScroll<HTMLDivElement>(mobileView === 'detail');

    const membersOf = (groupKey: string) => users.filter(u => groupsOf(u).includes(groupKey));
    const members = selectedGroup ? membersOf(selectedGroup.key) : [];
    const nonMembers = selectedGroup ? users.filter(u => !groupsOf(u).includes(selectedGroup.key)) : [];

    const changeMembership = async (user: Userprofile, groupKey: string, add: boolean) => {
        const current = groupsOf(user);
        const next = add ? [...current, groupKey] : current.filter(g => g !== groupKey);
        try {
            await setUserGroups(user.key, next);
            await onMembershipChanged();
        } catch {
            // The hook keeps the error for the alert below
        }
    };

    const handleAdd = async () => {
        if (!selectedGroup || !userToAdd) return;
        const user = users.find(u => u.key === userToAdd);
        if (!user) return;
        await changeMembership(user, selectedGroup.key, true);
        setUserToAdd('');
    };

    if (loading) {
        return <PageLoader text="Laddar användargrupper..." />;
    }

    return (
        <>
            {error && (
                <Alert variant="danger" dismissible onClose={clearError} className="mb-3">
                    {error}
                </Alert>
            )}
            <div className="manage-users-page" ref={containerRef} data-mobile-view={mobileView}>
                {/* Left Pane - Group list */}
                <div className="manage-users-left-pane" onClickCapture={rememberListScroll}>
                    <div className="left-pane-header" />
                    <div className="user-list-container">
                        {groups.length === 0 ? (
                            <div className="empty-user-list">Inga användargrupper</div>
                        ) : (
                            groups.map(group => (
                                <Link
                                    key={group.key}
                                    to={groupPath(group.key)}
                                    className={`user-item ${selectedGroup?.key === group.key ? 'selected' : ''}`}
                                    aria-current={selectedGroup?.key === group.key ? 'page' : undefined}
                                >
                                    <div className="user-item-avatar">
                                        <Users size={18} aria-hidden="true" />
                                    </div>
                                    <div className="user-item-info">
                                        <div className="user-item-name">{group.name}</div>
                                        <div className="user-item-meta">{memberCountLabel(membersOf(group.key).length)}</div>
                                    </div>
                                </Link>
                            ))
                        )}
                    </div>
                </div>

                {/* Right Pane - Group detail */}
                <div className="manage-users-right-pane">
                    {mobileView === 'detail' && (
                        <Link to={groupPath(null)} className="manage-users-back-button">
                            <ChevronLeft size={20} aria-hidden="true" />
                            Alla användargrupper
                        </Link>
                    )}
                    {selectedGroup ? (
                        <div className="user-detail-pane">
                            <div className="user-profile-header">
                                <div className="user-profile-avatar">
                                    <Users size={24} aria-hidden="true" />
                                </div>
                                <div className="user-profile-info">
                                    <h3 className="user-profile-name">{selectedGroup.name}</h3>
                                    <div className="user-profile-uid">{memberCountLabel(members.length)}</div>
                                </div>
                            </div>

                            <div className="user-detail-card">
                                <h6 className="user-detail-card-title">Lägg till användare</h6>
                                <div className="d-flex gap-2">
                                    <Form.Select
                                        size="sm"
                                        value={userToAdd}
                                        onChange={e => setUserToAdd(e.target.value)}
                                        disabled={submitting || nonMembers.length === 0}
                                        aria-label="Användare att lägga till"
                                    >
                                        <option value="">
                                            {nonMembers.length === 0 ? 'Alla användare är redan med' : 'Välj användare...'}
                                        </option>
                                        {nonMembers.map(u => (
                                            <option key={u.key} value={u.key}>{u.name}</option>
                                        ))}
                                    </Form.Select>
                                    <Button variant="primary" size="sm" onClick={handleAdd} disabled={!userToAdd || submitting}>
                                        <Plus size={16} className="me-1" />
                                        Lägg till
                                    </Button>
                                </div>
                            </div>

                            <div className="user-detail-card">
                                <h6 className="user-detail-card-title">Medlemmar</h6>
                                {members.length === 0 ? (
                                    <div className="text-muted">Inga användare i gruppen</div>
                                ) : (
                                    members.map(member => (
                                        <div key={member.key} className="user-item" style={{ cursor: 'default' }}>
                                            <div className={`user-item-avatar ${member.admin ? 'admin' : ''}`}>
                                                {getInitials(member.name)}
                                            </div>
                                            <div className="user-item-info">
                                                <div className="user-item-name">{member.name}</div>
                                            </div>
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => changeMembership(member, selectedGroup.key, false)}
                                                disabled={submitting}
                                                title={`Ta bort ${member.name} från ${selectedGroup.name}`}
                                                aria-label={`Ta bort ${member.name} från ${selectedGroup.name}`}
                                            >
                                                <X size={16} />
                                            </Button>
                                        </div>
                                    ))
                                )}
                            </div>

                            <Alert variant="info" className="mb-0">
                                <small>
                                    Användare i en grupp ser i iOS-appens Service- och inventeringsvyer bara de byggnader
                                    som gruppen ansvarar för. Användare utan grupp ser byggnader utan ansvarig grupp.
                                </small>
                            </Alert>
                        </div>
                    ) : (
                        <div className="empty-state">
                            <div className="text-muted">
                                Välj en grupp från listan för att se och ändra medlemmar
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default UserGroupsTab;
